import React from 'react'

import InverseColourBanner from 'components/Common/InverseColourBanner/InverseColourBanner'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useCuratedTrips } from 'tripPlanner/hooks/api/curatedTrip'
import CuratedTripPromotion from 'tripPlanner/Tripkit/CuratedTrips/TripPromotion/CuratedTripPromotion'
import { BasicTrip } from 'tripPlanner/types/common'
import { rem } from 'polished'
import styled from 'styled-components'

const Container = styled.div`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  
  &.padding-small {
    padding-top: ${rem(20)};
    padding-bottom: ${rem(20)}
  };
`

function findLocationName(
  offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary,
  trip: BasicTrip,
): string | undefined {
  /* The BasicTrip type doesn't have enough data for us to get the specific location that this offer is in.
  We can try to find that info by cross-referencing the location names from the BasicTrip with the location names in the offer.
  Not totally reliable, but it's better than having to fetch the full trip when the user might not end up viewing it. */
  const tripLocationNames = new Set(
    trip.metadata?.locations ? [
      ...trip.metadata.locations.map(l => l.lePlaceName),
      ...trip.metadata.locations.map(l => l.lePlaceName?.replace(/,.*$/, '')),
    ] : [],
  )
  const offerLocationNames = offer.type === 'bedbank_hotel' ?
      [offer.property.address.city] :
      [offer.locationHeading, ...offer.locations]
  return offerLocationNames.find(name => tripLocationNames.has(name))
}

interface Props {
  offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary
  verticalPaddingSize?: 'small' | 'large'
}

function OfferTripPromotion({ offer, verticalPaddingSize = 'large' }: Props) {
  const { data: trips = EmptyArray as Array<BasicTrip> } = useCuratedTrips({
    filters: { sourceItemCode: offer.id },
    limit: 1,
  })

  const hasTrips = trips.length > 0

  if (!hasTrips) {
    return null
  }

  const trip = trips[0]
  const locationName = findLocationName(offer, trip)
  const heading = `What to do ${locationName ? `in ${locationName}` : ''} while staying at this hotel`
  const subheading = 'All the planning has been taken care of with this curated trip' + (offer.property ? ` to ${offer.property.name}` : '')

  return (
    <InverseColourBanner>
      <Container className={`padding-${verticalPaddingSize}`} >
        <CuratedTripPromotion
          heading={heading}
          subheading={subheading}
          trip={trip}
        />
      </Container>
    </InverseColourBanner>
  )
}

export default OfferTripPromotion
